/**
 * Web Chat e canal de API no assistente (29/09/2026), no formato do motor de
 * conversa: um formulário curto, e depois o jeito de instalar.
 *
 * O Web Chat pede o nome e o site; ao fim, o código que vai no site. O canal de
 * API pede o nome e, se quiser, o endereço onde o outro sistema recebe as
 * respostas; ao fim, o identificador e o endereço para mandar mensagens.
 */

import { useId, useState, type ReactNode } from 'react';
import { useMutation } from '@tanstack/react-query';
import type { CanalConectado } from '@cliqcrm/contracts';
import { Botao, Campo } from '@/shared/ui';
import { Faixa } from '@/shared/ui/estados/faixa';
import { useApi } from '@/shared/api/consultas';
import { recadoDeErro } from '@/shared/api/recado-de-erro';

export type TipoSimples = 'web_chat' | 'api';

/** O roxo da marca: a cor do chat quando a revenda não escolhe outra. */
const COR_PADRAO = '#4f3d99';

export function ConfigurarCanalSimples({
  tipo,
  aoCriar,
  aoEscolherOutro,
}: {
  tipo: TipoSimples;
  aoCriar: (canal: CanalConectado) => void;
  aoEscolherOutro: () => void;
}): ReactNode {
  const api = useApi();
  const [nome, definirNome] = useState(tipo === 'web_chat' ? 'Chat do site' : '');
  const [endereco, definirEndereco] = useState('');
  // Os campos do Web Chat que o motor pede na criação (30/09/2026).
  const [cor, definirCor] = useState(COR_PADRAO);
  const idDaCor = useId();
  const [titulo, definirTitulo] = useState('Olá! Como podemos ajudar?');
  const [frase, definirFrase] = useState(
    'Fale com a gente por aqui. Respondemos o mais rápido possível.',
  );
  const [erro, definirErro] = useState<string | null>(null);

  const criar = useMutation({
    mutationFn: () =>
      tipo === 'web_chat'
        ? api!.criarWebChat({
            corpo: {
              nome: nome.trim(),
              site: endereco.trim(),
              cor,
              titulo_de_boas_vindas: titulo.trim(),
              frase_de_boas_vindas: frase.trim(),
            },
          })
        : api!.criarCanalDeApi({
            corpo: {
              nome: nome.trim(),
              ...(endereco.trim() ? { webhook_url: endereco.trim() } : {}),
            },
          }),
    onMutate: () => definirErro(null),
    onSuccess: aoCriar,
    onError: (falha: unknown) =>
      definirErro(
        recadoDeErro(falha, { generica: 'Não foi possível criar o canal. Tente de novo.' }),
      ),
  });

  const siteValido = /^https?:\/\/\S+\.\S+/.test(endereco.trim());
  const avisoValido = endereco.trim() === '' || /^https:\/\/\S+\.\S+/.test(endereco.trim());
  const pronto = nome.trim().length >= 2 && (tipo === 'web_chat' ? siteValido : avisoValido);

  return (
    <div className="passos-canal__corpo">
      <p className="admin__apoio">
        {tipo === 'web_chat'
          ? 'O chat aparece no canto do site da sua empresa. Quem escreve por ali cai no Atendimento, ' +
            'como qualquer outra conversa.'
          : 'Para outro sistema da sua empresa conversar pelo Atendimento: ele manda as mensagens do ' +
            'cliente por este canal e recebe as respostas no endereço de aviso.'}
      </p>
      <Campo
        rotulo="Nome do canal"
        ajuda="É como ele aparece no Atendimento e na lista de canais."
        value={nome}
        maxLength={60}
        onChange={(e) => definirNome(e.target.value)}
      />
      {tipo === 'web_chat' ? (
        <div className="web-chat-form">
          <div className="web-chat-form__campos">
            <Campo
              rotulo="Endereço do site"
              ajuda="Onde o chat vai aparecer. Exemplo: https://www.suaempresa.com.br"
              type="url"
              inputMode="url"
              placeholder="https://"
              value={endereco}
              onChange={(e) => definirEndereco(e.target.value)}
            />
            <div className="web-chat-form__cor">
              <label className="web-chat-form__rotulo" htmlFor={idDaCor}>
                Cor do chat
              </label>
              <input
                id={idDaCor}
                type="color"
                value={cor}
                onChange={(e) => definirCor(e.target.value)}
              />
              <span className="web-chat-form__hex" aria-hidden="true">
                {cor}
              </span>
            </div>
            <Campo
              rotulo="Título de boas-vindas"
              ajuda="Aparece no topo quando o visitante abre o chat."
              value={titulo}
              maxLength={80}
              onChange={(e) => definirTitulo(e.target.value)}
            />
            <Campo
              rotulo="Frase de boas-vindas"
              value={frase}
              maxLength={255}
              onChange={(e) => definirFrase(e.target.value)}
            />
          </div>
          {/* A prévia, como na criação do motor: o visitante vê isto ao abrir. */}
          <div className="web-chat-previa" aria-hidden="true">
            <div className="web-chat-previa__topo" style={{ background: cor }}>
              <strong>{titulo.trim() || 'Olá!'}</strong>
              <span>{frase.trim()}</span>
            </div>
            <div className="web-chat-previa__corpo" />
            <span className="web-chat-previa__botao" style={{ background: cor }} />
          </div>
        </div>
      ) : (
        <Campo
          rotulo="Endereço de aviso (opcional)"
          ajuda="Para onde as respostas do Atendimento são enviadas. Precisa começar com https://."
          type="url"
          inputMode="url"
          placeholder="https://"
          value={endereco}
          onChange={(e) => definirEndereco(e.target.value)}
        />
      )}
      <div className="passos-canal__acoes">
        <Botao
          variante="primario"
          desabilitado={!pronto || criar.isPending || api === null}
          motivo={
            api === null
              ? 'Sem conexão agora. Tente de novo em instantes.'
              : !pronto
                ? tipo === 'web_chat'
                  ? 'Preencha o nome e o endereço do site.'
                  : 'Preencha o nome; o endereço de aviso, se houver, precisa ser https://.'
                : 'Criando…'
          }
          onClick={() => criar.mutate()}
        >
          {criar.isPending ? 'Criando…' : tipo === 'web_chat' ? 'Criar Web Chat' : 'Criar canal'}
        </Botao>
        <Botao onClick={aoEscolherOutro}>Escolher outro canal</Botao>
      </div>
      {erro && <Faixa tom="bloqueado">{erro}</Faixa>}
    </div>
  );
}

/**
 * Como instalar o canal: o código do Web Chat, ou o identificador da API.
 *
 * O endereço da API é o deste site: o motor atende a API pública pelo nosso
 * domínio, e a borda publica só esse caminho.
 */
export function InstalacaoDoCanal({
  tipo,
  instalacao,
}: {
  tipo: TipoSimples;
  instalacao: string;
}): ReactNode {
  const [copiado, definirCopiado] = useState(false);
  const texto =
    tipo === 'web_chat'
      ? instalacao.trim()
      : `${location.origin}/public/api/v1/inboxes/${instalacao}`;

  const copiar = () => {
    void navigator.clipboard?.writeText(texto).then(
      () => definirCopiado(true),
      () => definirCopiado(false),
    );
  };

  return (
    <div className="instalacao-canal">
      {tipo === 'web_chat' ? (
        <p className="admin__apoio">
          Cole este código no site, logo antes de <code>&lt;/body&gt;</code>. O chat aparece na
          próxima vez que a página abrir.
        </p>
      ) : (
        <p className="admin__apoio">
          O outro sistema manda as mensagens para este endereço. O identificador do canal é{' '}
          <code>{instalacao}</code>.
        </p>
      )}
      <pre className="instalacao-canal__codigo" tabIndex={0} aria-label="Código de instalação">
        <code>{texto}</code>
      </pre>
      <div className="passos-canal__acoes">
        <Botao onClick={copiar}>{copiado ? 'Copiado' : 'Copiar'}</Botao>
      </div>
      {copiado && (
        <p className="visualmente-oculto" role="status">
          Copiado para a área de transferência.
        </p>
      )}
    </div>
  );
}
